import type { ReactNode } from "react";
import { Routes, Route, Navigate, Outlet, useLocation } from "react-router-dom";
import { AuthRoute } from "@/core/components/auth-route";
import { ProtectedRoute } from "@/core/components/protected-route";
import { PublicRoute } from "@/core/components/public-route";
import { Toaster } from "@volca/ui/components/ui/toast";
import { TooltipProvider } from "@volca/ui/components/ui/tooltip";
import { AppShell } from "@/app-shell";
import { getProductCapabilities } from "@/business/product-capabilities";
import ChangelogPage from "@/changelog/changelog-page";
import { CHANGELOG_PATH } from "@/changelog/routes";
import { useAuth } from "@/core/auth";
import { useBusinessProducts } from "@/core/business/use-business-products";
import NotFound from "@/not-found";
import { ProductMarketingBoundary } from "@/product-marketing/product-marketing-boundary";
import Referrals from "@/referrals";
import { useGetMyUser } from "@/user/query";
import { isApOnly } from "@/user/utils";
import { WaitingForPhoneNumber, WaitingForSetup } from "@/waiting-pages";
import AccountsPayable from "./accounts-payable";
import AccountsPayableInvoicePage from "./accounts-payable/invoice-review-page";
import PublicDocumentPage from "./accounts-payable/public-document-page";
import AccountsPayableReceiptPage from "./accounts-payable/receipt-review-page";
import AdvocateSignupRedirect from "./advocate-signup-redirect";
import BusinessSettings from "./business-settings";
import Business from "./business-settings/business";
import BusinessSettingsRedirect from "./business-settings/business-settings-redirect";
import Customers from "./business-settings/customers";
import Payments from "./business-settings/payments";
import { QuickbooksCallback } from "./business-settings/quickbooks-callback";
import ReferralHistory from "./business-settings/referral-history";
import ReferralSources from "./business-settings/referral-sources";
import { Reviews } from "./business-settings/reviews";
import TextMessages from "./business-settings/text-messages";
import Users from "./business-settings/users";
import { Campaigns } from "./campaigns";
import ClaimPayout from "./claim-payout";
import { useProductionWarning } from "./core/env/use-product-warning";
import { PylonProvider } from "./core/pylon";
import { usePylonAccess } from "./core/pylon/use-pylon-access";
import Dashboard from "./dashboard";
import ForgotPassword from "./forgot-password";
import HelpPage from "./help/help-page";
import { HELP_PATH } from "./help/routes";
import IcmStatementPage from "./icm/icm-statement-page";
import Login from "./login";
import { OAuthCallback } from "./oauth-callback";
import Onboarding from "./onboarding";
import { SIMPLE_ONBOARDING_PATHS } from "./onboarding/routes";
import PayrollEditorPage from "./payroll/editor/payroll-editor-page";
import PayBreakdownPage from "./payroll/pay-breakdown/pay-breakdown-page";
import PayrollStatementNotificationsPage from "./payroll/pay-breakdown/payroll-statement-notifications";
import { PAY_BREAKDOWN_PATH } from "./payroll/pay-breakdown/routes";
import PayrollRosterPage from "./payroll/roster";
import PayrollSettings from "./payroll/settings";
import PayrollSettingsIndex from "./payroll/settings/payroll-settings-index";
import {
  LEGACY_PAYROLL_EDITOR_PATH,
  PAYROLL_EDITOR_PATH,
  PAYROLL_SETTINGS_PATH,
} from "./payroll/settings/routes";
import WorkbookAccessPage from "./payroll/workbook-access";
import PrivacyPolicy from "./privacy-policy";
import Profile from "./profile";
import { profitabilityRoutes } from "./profitability";
import ResetPassword from "./reset-password";
import ResetPasswordCode from "./reset-password-code";
import Signup from "./signup";
import TechFeedback from "./tech-feedback";
import TestError from "./test-error";

const Frame = ({
  isStandalone,
  children,
}: {
  isStandalone: boolean;
  children: ReactNode;
}) =>
  isStandalone ? (
    <main className="h-screen w-full overflow-y-auto">{children}</main>
  ) : (
    <AppShell>{children}</AppShell>
  );

const AppContent = () => {
  const hasPylonAccess = usePylonAccess();
  const { isAuthenticated } = useAuth();
  useProductionWarning();
  const location = useLocation();
  const isPayoutRoute = location.pathname.startsWith("/me/");
  const { settings, isLoading: isProductsLoading } = useBusinessProducts();
  const { data: user } = useGetMyUser();
  const apOnly = isApOnly(user?.role);
  const capabilities = getProductCapabilities({ user, settings });

  return (
    <TooltipProvider delay={700}>
      {/* The payout claim pages (/me/…) stand alone, without the app's frame. */}
      <PylonProvider hasAccess={hasPylonAccess}>
        <Frame isStandalone={isPayoutRoute}>
          <Routes>
            {/* Protected routes group */}
            <Route
              element={
                <ProtectedRoute>
                  <ProductMarketingBoundary>
                    <Outlet />
                  </ProductMarketingBoundary>
                </ProtectedRoute>
              }
            >
              <Route index element={<Navigate to="/dashboard" replace />} />
              <Route
                path={SIMPLE_ONBOARDING_PATHS.root}
                element={
                  <Navigate to={SIMPLE_ONBOARDING_PATHS.welcome} replace />
                }
              />
              <Route
                path={`${SIMPLE_ONBOARDING_PATHS.root}/:pane`}
                element={<Onboarding />}
              />
              <Route path="/dashboard" element={<Dashboard />} />
              {profitabilityRoutes()}
              {/* Signed out, mount them anyway so ProtectedRoute sends a deep
                link to sign in rather than to "Page not found". */}
              {(!user ||
                capabilities.enabled.accounts_payable ||
                isProductsLoading ||
                apOnly ||
                capabilities.canPreviewProducts) && (
                <>
                  <Route
                    path="/accounts-payable"
                    element={<AccountsPayable />}
                  />
                  {/* One route per tab: review, automated, invoices, receipts,
                    emails, vendors. */}
                  <Route
                    path="/accounts-payable/:tab"
                    element={<AccountsPayable />}
                  />
                  {isAuthenticated && (
                    <>
                      <Route
                        path="/accounts-payable/invoices/:invoiceId"
                        element={<AccountsPayableInvoicePage />}
                      />
                      <Route
                        path="/accounts-payable/receipts/:receiptId"
                        element={<AccountsPayableReceiptPage />}
                      />
                    </>
                  )}
                </>
              )}
              <Route path="/referrals" element={<Referrals />} />
              <Route path="/campaigns" element={<Campaigns />} />
              <Route path="/profile" element={<Profile />} />
              <Route path={CHANGELOG_PATH} element={<ChangelogPage />} />
              <Route path={`${HELP_PATH}/*`} element={<HelpPage />} />
              <Route
                path="/business-settings"
                element={<BusinessSettingsRedirect />}
              />
              <Route
                path="/business-settings/:businessId"
                element={<BusinessSettings />}
              >
                <Route index element={<Business />} />
                <Route path="users/:tab?" element={<Users />} />
                <Route path="customers/:tab?" element={<Customers />} />
                <Route path="payments/:tab?" element={<Payments />} />
                <Route path="referral-history" element={<ReferralHistory />} />
                <Route path="text-messages/:tab?" element={<TextMessages />} />
                <Route path="referral-sources" element={<ReferralSources />} />
                <Route path="reviews" element={<Reviews />} />
              </Route>
              <Route
                path="/payroll"
                element={<Navigate to={PAY_BREAKDOWN_PATH} replace />}
              />
              <Route path={PAY_BREAKDOWN_PATH} element={<PayBreakdownPage />} />
              {/* A two-pane working surface of its own, outside Settings. */}
              <Route
                path={PAYROLL_EDITOR_PATH}
                element={<PayrollEditorPage />}
              />
              {/* Links from before the workspace left the URL, and the older
                  Settings path, both land on the one screen. */}
              <Route
                path={`${PAYROLL_EDITOR_PATH}/*`}
                element={<Navigate to={PAYROLL_EDITOR_PATH} replace />}
              />
              <Route
                path={`${LEGACY_PAYROLL_EDITOR_PATH}/*`}
                element={<Navigate to={PAYROLL_EDITOR_PATH} replace />}
              />
              <Route path={PAYROLL_SETTINGS_PATH} element={<PayrollSettings />}>
                <Route index element={<PayrollSettingsIndex />} />
                <Route path="roster" element={<PayrollRosterPage />} />
                <Route
                  path="workbook-access"
                  element={<WorkbookAccessPage />}
                />
              </Route>
              <Route
                path={`${PAY_BREAKDOWN_PATH}/:periodStart/notifications`}
                element={<PayrollStatementNotificationsPage />}
              />
              <Route
                path={`${PAY_BREAKDOWN_PATH}/:periodStart`}
                element={<PayBreakdownPage />}
              />
              <Route
                path={`${PAY_BREAKDOWN_PATH}/:periodStart/:section`}
                element={<PayBreakdownPage />}
              />
              <Route
                path={`${PAY_BREAKDOWN_PATH}/:periodStart/statements/:statementId`}
                element={<IcmStatementPage />}
              />
              <Route
                path={`${PAY_BREAKDOWN_PATH}/statements/:statementId`}
                element={<IcmStatementPage />}
              />
            </Route>
            {/* Auth routes group (directs to app if already logged in) */}
            <Route
              element={
                <AuthRoute>
                  <Outlet />
                </AuthRoute>
              }
            >
              <Route path="/login" element={<Login />} />
              <Route path="/signup" element={<Signup />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route
                path="/reset-password-code"
                element={<ResetPasswordCode />}
              />
              <Route path="/reset-password" element={<ResetPassword />} />
            </Route>
            {/* Other public routes */}
            <Route
              element={
                <PublicRoute>
                  <Outlet />
                </PublicRoute>
              }
            >
              <Route path="/waiting-for-setup" element={<WaitingForSetup />} />
              <Route
                path="/waiting-for-phone-number"
                element={<WaitingForPhoneNumber />}
              />
              <Route
                path="/get-volca"
                element={<Navigate to={PAY_BREAKDOWN_PATH} replace />}
              />
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
              <Route path="/tech-feedback" element={<TechFeedback />} />
              <Route
                path="/advocate-signup-redirect"
                element={<AdvocateSignupRedirect />}
              />
              <Route path="/oauth/callback" element={<OAuthCallback />} />
              <Route
                path="/quickbooks/callback"
                element={<QuickbooksCallback />}
              />
              <Route path="/me/:payoutEndpoint" element={<ClaimPayout />} />
              <Route path="/test-error" element={<TestError />} />
              {!isAuthenticated && (
                <>
                  <Route
                    path="/accounts-payable/invoices/:documentId"
                    element={<PublicDocumentPage kind="invoice" />}
                  />
                  <Route
                    path="/accounts-payable/receipts/:documentId"
                    element={<PublicDocumentPage kind="receipt" />}
                  />
                </>
              )}
              <Route path="*" element={<NotFound />} />
            </Route>
          </Routes>
        </Frame>
      </PylonProvider>
      <Toaster />
    </TooltipProvider>
  );
};

export default AppContent;
